import { UploadFile, UploadProps } from 'antd';
import { b64toBlob, getBase64, openLinkInNewTab } from './utils';
import { RcFile } from 'antd/lib/upload';
import { getAWSSignedURL } from '@app/api/file.api';
import { IFile } from '@app/interfaces/file';
import { notificationController } from '@app/controllers/notificationController';

interface FilePropsInterface {
  maxCount: number;
  multiple: boolean;
  accept?: string | null;
  fileList: UploadFile[];
  setList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  handlePreview: (file: UploadFile) => void;
}

export const isFilePdf = (file: string): boolean => {
  const pdfRegex = /\.pdf$/i;
  return pdfRegex.test(file.toLowerCase());
};

export const isFileImage = (file: string): boolean => {
  const imageRegex = /\.(jpeg|jpg|gif|png|svg|webp)$/i;
  return imageRegex.test(file.toLowerCase());
};

export const isFileViewable = (fileUrl: string): boolean => {
  if (!fileUrl) return false;

  // 1. Strip query parameters (essential for S3 Signed URLs)
  const urlWithoutQuery = fileUrl.split('?')[0];

  // 2. Extract extension safely
  const extension = urlWithoutQuery.split('.').pop()?.toLowerCase() || '';

  // 3. Define Browser-Native formats
  const browserSupportedImages = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const browserSupportedMedia = ['mp3', 'wav', 'ogg', 'mp4', 'webm'];
  const browserSupportedDocs = ['pdf', 'txt', 'pdf'];

  // Check against your FILE_TYPES categories for consistency
  return (
    browserSupportedImages.includes(extension) ||
    browserSupportedMedia.includes(extension) ||
    browserSupportedDocs.includes(extension) ||
    ['json', 'html', 'htm'].includes(extension)
  );
};

export const closeTab = (tab: Window, isFalse = false): void => {
  setTimeout(
    () => {
      tab.close();
    },
    isFalse ? 0 : 1000,
  );
};

export const getFileProps = ({
  maxCount,
  multiple,
  accept = null,
  fileList,
  setList,
  handlePreview,
}: FilePropsInterface): UploadProps => {
  const fileProps: UploadProps = {
    multiple: multiple,
    showUploadList: true,
    listType: 'picture',
    fileList,
    beforeUpload: () => {
      return false;
    },
    isImageUrl: () => {
      return false;
    },
    onChange: (info: { fileList: React.SetStateAction<UploadFile<unknown>[]> }) => {
      setList(info.fileList);
    },
    onPreview: handlePreview,
  };

  if (maxCount) fileProps.maxCount = maxCount;
  if (accept) fileProps.accept = accept;

  return fileProps;
};

export const handlePreview = async (file: UploadFile): Promise<void> => {
  if (!file.url) {
    const newWindow = window.open('', '_blank');
    const base64 = await getBase64(file.originFileObj as RcFile);
    file.preview = base64;
    const code = base64.split(';base64,')[1];
    const blob = b64toBlob(code, file.type);
    const url = URL.createObjectURL(blob);
    openLinkInNewTab(newWindow, url);
  } else {
    openFile(file.uid);
  }
};

export const openFile = async (file: IFile | string): Promise<void> => {
  const newWindow = window.open('', '_blank');
  const fileId = typeof file === 'string' ? file : file.id;
  try {
    const url = await getAWSSignedURL(fileId);
    openLinkInNewTab(newWindow, url);
    if (!isFileViewable(url as string) && newWindow) {
      closeTab(newWindow);
    }
  } catch (error) {
    if (newWindow) closeTab(newWindow);
    notificationController.error({ message: (error as Error).message });
  }
};

export const downloadFile = async (file: IFile): Promise<void> => {
  const url = await getAWSSignedURL(file.id);
  if (url) {
    const link = document.createElement('a');
    link.href = url;
    link.download = file.originalName as string;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
